import {
  Zap, Layers, Trophy, ChevronsRight, TrendingUp,
  Globe2, Cpu, GitCompare, Users, BookOpen, ScrollText, Mail, Info,
  Sparkles, Calculator, Award, FileCheck, Rocket, ShieldCheck, Newspaper,
} from "lucide-react";
import { PLATFORMS } from "@/data/site";

/* Mega-menu structure — SEO-friendly subpages.
   Each dropdown column has a heading + 2-6 link items with icon + tagline.
   Each section can ALSO carry a `spotlight` block — a flashy CTA tile
   rendered as a right-rail in the ribbon to make the menu feel
   product-y, not just a link dump. */
export const MEGA_MENU = [
  {
    key: "challenges",
    label: "Challenges",
    // Two-step picker: left rail has the three challenge TYPES, right
    // pane reveals sizes for the active type. Renders via a custom
    // `picker: "challenges"` branch in MegaRibbon (see Navbar.jsx).
    picker: "challenges",
    programs: [
      {
        id: "instant",
        // Branded icon name — rendered as a lightning bolt badge in Navbar.
        iconKind: "bolt",
        label: "Instant Funding",
        blurb: "Funded from day one — no eval",
        accent: "from-amber-300 to-amber-500",
        sizes: [
          { to: "/?p=instant&s=5000#challenges",   label: "$5K",   price: "$250",   tier: "starter" },
          { to: "/?p=instant&s=10000#challenges",  label: "$10K",  price: "$500",   tier: "standard" },
          { to: "/?p=instant&s=25000#challenges",  label: "$25K",  price: "$1,250", tier: "mid" },
          { to: "/?p=instant&s=50000#challenges",  label: "$50K",  price: "$2,500", tier: "popular" },
          { to: "/?p=instant&s=100000#challenges", label: "$100K", price: "$5,000", tier: "pro" },
        ],
      },
      {
        id: "oneStep",
        // Rendered as a numeric "1" badge in Navbar.
        iconKind: "one",
        label: "1-Step Challenge",
        blurb: "Single profit target — fast track",
        accent: "from-sky-300 to-cyan-400",
        sizes: [
          { to: "/?p=oneStep&s=10000#challenges",  label: "$10K",  price: "$99",  tier: "starter" },
          { to: "/?p=oneStep&s=25000#challenges",  label: "$25K",  price: "$199", tier: "standard" },
          { to: "/?p=oneStep&s=50000#challenges",  label: "$50K",  price: "$399", tier: "popular" },
          { to: "/?p=oneStep&s=100000#challenges", label: "$100K", price: "$750", tier: "pro" },
        ],
      },
      {
        id: "twoStep",
        // Rendered as a numeric "2" badge in Navbar.
        iconKind: "two",
        label: "2-Step Challenge",
        blurb: "Classic evaluation — lowest cost",
        accent: "from-emerald-300 to-teal-400",
        sizes: [
          { to: "/?p=twoStep&s=10000#challenges",  label: "$10K",  price: "$49",    tier: "starter" },
          { to: "/?p=twoStep&s=25000#challenges",  label: "$25K",  price: "$125",   tier: "standard" },
          { to: "/?p=twoStep&s=50000#challenges",  label: "$50K",  price: "$225",   tier: "popular" },
          { to: "/?p=twoStep&s=100000#challenges", label: "$100K", price: "$450",   tier: "pro" },
          { to: "/?p=twoStep&s=250000#challenges", label: "$250K", price: "$1,250", tier: "max" },
        ],
      },
    ],
    spotlight: {
      kind: "offer",
      eyebrow: "Challenge Builder",
      title: "Build your challenge, your way",
      body: "Instant, 1-Step or 2-Step — pick your platform, account size and add-ons in seconds.",
      cta: { label: "Start building", to: "/#challenges" },
      icon: Rocket,
    },
  },
  {
    key: "platforms",
    label: "Trading Platforms",
    columns: [
      {
        heading: "Platforms",
        items: [
          // Platform menu items render with the platform's own logo
          // and brand accent (see Navbar.jsx item-render branch). The
          // Lucide `Icon` field is kept as a fallback only.
          { to: "/platforms/ctrader",     platformId: "ctrader",     Icon: Zap,    label: "cTrader",     tagline: "ECN, native DOM, cBots" },
          { to: "/platforms/dxtrade",     platformId: "dxtrade",     Icon: Globe2, label: "DXTrade",     tagline: "Browser-native, multi-asset" },
          { to: "/platforms/matchtrader", platformId: "matchtrader", Icon: Layers, label: "MatchTrader", tagline: "Mobile-first matching" },
          { to: "/platforms/gooeypro",    platformId: "gooeypro",    Icon: Cpu,    label: "GooeyPro",    tagline: "Cloud-powered execution" },
        ],
      },
      {
        heading: "Choose wisely",
        items: [
          { to: "/platforms/compare",   Icon: GitCompare, label: "Compare Platforms", tagline: "Side-by-side features" },
          { to: "/platforms/web",       Icon: Globe2,     label: "Web Trading",       tagline: "No install, any device" },
          { to: "/platforms/mobile",    Icon: Sparkles,   label: "Mobile Apps",       tagline: "iOS + Android" },
        ],
      },
    ],
    spotlight: {
      kind: "platforms",
      eyebrow: "4 venues · 1 rulebook",
      title: "Identical rules across every platform",
      body: "Same drawdown, same targets, same payouts. Pick the venue you trade best on.",
      cta: { label: "Compare all four", to: "/platforms/compare" },
      icon: ShieldCheck,
      platforms: PLATFORMS,
    },
  },
  {
    key: "affiliates",
    label: "Affiliates",
    columns: [
      {
        heading: "Earn with OFunded",
        items: [
          { to: "/partner",               Icon: Users,    label: "Partner Program",     tagline: "10% lifetime commission" },
          { to: "/affiliates/influencers",Icon: Trophy,   label: "Influencers",         tagline: "Dedicated team & assets" },
          { to: "/affiliates/dashboard",  Icon: TrendingUp, label: "Affiliate Dashboard", tagline: "Track clicks & payouts" },
        ],
      },
      {
        heading: "Resources",
        items: [
          { to: "/partner#partner-stack-banner", Icon: Award,    label: "Commission & Discount", tagline: "10% lifetime + 10% audience discount" },
          { to: "/partner#partner-resources",    Icon: BookOpen, label: "Creative Assets",       tagline: "Banners, copy, B-roll" },
          { to: "/contact",                      Icon: Mail,     label: "Talk to Partners",      tagline: "Custom deals for IBs" },
        ],
      },
    ],
    spotlight: {
      kind: "offer",
      eyebrow: "Partner stack",
      title: "10% lifetime + 10% audience discount",
      body: "Flat 10% lifetime commission on every referral, plus a negotiable 1-month discount code for your audience.",
      cta: { label: "Join the program", to: "/partner" },
      icon: Trophy,
    },
  },
  {
    key: "more",
    label: "More",
    columns: [
      {
        heading: "Company",
        items: [
          { to: "/about",       Icon: Info,    label: "About OFunded", tagline: "Who we are, where we operate" },
          { to: "/company-news",Icon: Newspaper,label: "Company News", tagline: "Press releases & milestones" },
          { to: "/how-it-works",Icon: BookOpen,label: "How It Works",  tagline: "From challenge to payout" },
          { to: "/contact",     Icon: Mail,    label: "Contact",       tagline: "Get in touch" },
        ],
      },
      {
        heading: "Challenge helpers",
        items: [
          { to: "/calculator",       Icon: Calculator, label: "Profit Calculator", tagline: "Project your payout" },
          { to: "/general-rules",    Icon: ScrollText, label: "General Rules",     tagline: "Drawdown, payouts, allowed" },
          { to: "/payout-protector", Icon: Award,      label: "Payout Protector",  tagline: "Insure your first payout" },
        ],
      },
      {
        heading: "Resources",
        items: [
          { to: "/faq",            Icon: FileCheck,  label: "FAQ",             tagline: "60+ answered questions" },
          { to: "/blog",           Icon: BookOpen,   label: "Blog & Insights", tagline: "Trader stories & guides" },
          { to: "/legal/terms",    Icon: ScrollText, label: "Legal & Policies",tagline: "Terms, privacy, refund" },
        ],
      },
    ],
    spotlight: {
      kind: "offer",
      eyebrow: "New here?",
      title: "Read the 4-step playbook",
      body: "Pick a challenge, pass the eval, get funded, withdraw up to 90% of gains.",
      cta: { label: "How it works", to: "/how-it-works" },
      icon: BookOpen,
    },
  },
];
